/**
 * Cabecera de "Actualizar tickets" reutilizable en dashboard, /tickets,
 * proyectos y compañeros. Muestra:
 *
 *   - Botón "Actualizar" con icono. Mientras `isPending`, muestra spinner.
 *   - Texto contextual: cuándo se sincronizó por última vez y si fue
 *     manual (usuario clickeó) o automático (cron).
 *
 * Si nunca se ha cacheado (meta=null) el botón sigue funcionando pero
 * el texto cae a "Sin sincronización previa".
 */
import { RefreshCw } from "lucide-react";

export type RefreshMeta = {
  cachedAt: number; // unix seconds
  source: "manual" | "auto";
} | null;

export function PlaneRefreshHeader({
  meta,
  isPending,
  onRefresh,
  className = "",
}: {
  meta: RefreshMeta;
  isPending: boolean;
  onRefresh: () => void;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 text-xs text-base-content/55 ${className}`}>
      <button
        type="button"
        onClick={onRefresh}
        disabled={isPending}
        className="btn btn-ghost btn-xs gap-1.5"
        title="Forzar sync con Plane ahora"
      >
        {isPending ? (
          <span className="loading loading-spinner loading-xs" />
        ) : (
          <RefreshCw className="size-3.5" />
        )}
        Actualizar
      </button>
      <span className="opacity-70">{describeMeta(meta)}</span>
    </div>
  );
}

function describeMeta(meta: RefreshMeta): string {
  if (!meta) return "Sin sincronización previa";
  const ageSec = Math.max(0, Math.floor(Date.now() / 1000 - meta.cachedAt));
  const label = meta.source === "manual" ? "manual" : "automática";
  return `Hace ${formatAge(ageSec)} · sync ${label}`;
}

function formatAge(sec: number): string {
  if (sec < 60) return "menos de 1min";
  if (sec < 60 * 60) return `${Math.floor(sec / 60)}min`;
  if (sec < 24 * 60 * 60) {
    const h = Math.floor(sec / 3600);
    return h === 1 ? "1h" : `${h}h`;
  }
  const d = Math.floor(sec / (24 * 3600));
  return d === 1 ? "1 día" : `${d} días`;
}
